import { Box, Typography, Button, Breadcrumbs, CircularProgress, Switch, Grid } from '@mui/material';
import { StandaloneFormInlineInput } from 'ui-component/formComponents/Sections/StandaloneFormInlineInput';
import { Notification, NotificationFormData } from '../../types';
import { useNotificationForm } from '../../hooks/useNotificationForm';
import { ConditionSection } from './ConditionSection';
import { SimpleAutocomplete } from './SimpleAutocomplete';
import { SimpleTextField } from './SimpleTextField';

interface NotificationFormProps {
    notification: Notification | null;
    onClose: () => void;
    onSave: (data: NotificationFormData) => Promise<void>;
}

const NotificationForm = ({ notification, onClose, onSave }: NotificationFormProps) => {
    const {
        formData,
        showCondition,
        recordTypes,
        events,
        fields,
        loadingEvents,
        loadingFields,
        canApplyCondition,
        isLoading,
        isFormValid,
        isDuplicate,
        getBreadcrumbTitle,
        handleConditionToggle,
        handleConditionChange,
        handleFormChange,
        handleRecordTypeChange,
        handleSubmit
    } = useNotificationForm({ notification, onClose, onSave });

    return (
        <Box
            data-testid="notification-form-container"
            sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
        >
            {/* Header with Breadcrumb */}
            <Box sx={{ px: 3, pt: 2, pb: 2 }}>
                <Breadcrumbs
                    data-testid="notification-form-breadcrumbs"
                    separator=">"
                    sx={{
                        mb: 0,
                        '& .MuiBreadcrumbs-separator': { mx: 1, color: '#6B7280' },
                        '& .MuiBreadcrumbs-li': { display: 'flex', alignItems: 'center' }
                    }}
                >
                    <Typography
                        data-testid="notification-form-breadcrumb-back"
                        component="button"
                        variant="body2"
                        onClick={onClose}
                        sx={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'primary.main',
                            textDecoration: 'none',
                            p: 0,
                            fontSize: '16px'
                        }}
                    >
                        Notifications
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'primary.main',
                            fontSize: '16px'
                        }}
                    >
                        {getBreadcrumbTitle()[0]}{' '}
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                            {getBreadcrumbTitle()[1]}
                        </Typography>
                    </Typography>
                </Breadcrumbs>
            </Box>

            {/* Form Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                <Grid container spacing={2}>
                    {/* Row 1: Name and Record Type */}
                    <Grid item xs={12} md={6}>
                        <StandaloneFormInlineInput
                            label="Name"
                            formComponent={
                                <SimpleTextField
                                    data-testid="notification-form-name-input"
                                    fullWidth
                                    value={formData.name}
                                    onChange={(e) => handleFormChange('name', e.target.value)}
                                    size="small"
                                    variant="outlined"
                                />
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <StandaloneFormInlineInput
                            label={
                                <>
                                    Record Type <span style={{ color: '#FF0000' }}>*</span>
                                </>
                            }
                            formComponent={
                                <SimpleAutocomplete
                                    data-testid="notification-form-record-type-select"
                                    value={formData.recordTypeId}
                                    onChange={handleRecordTypeChange}
                                    options={recordTypes}
                                    error={!formData.recordTypeId}
                                    placeholder="Required Field"
                                />
                            }
                        />
                    </Grid>

                    {/* Row 2: Trigger/event and Priority */}
                    <Grid item xs={12} md={6}>
                        <StandaloneFormInlineInput
                            label={
                                <>
                                    Trigger/event <span style={{ color: '#FF0000' }}>*</span>
                                </>
                            }
                            formComponent={
                                <SimpleAutocomplete
                                    data-testid="notification-form-event-select"
                                    value={formData.eventId}
                                    onChange={(value) => handleFormChange('eventId', value)}
                                    options={events}
                                    error={!formData.eventId}
                                    disabled={!formData.recordTypeId || loadingEvents}
                                    placeholder="Required Field"
                                />
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <StandaloneFormInlineInput
                            label="Priority"
                            formComponent={
                                <SimpleAutocomplete
                                    data-testid="notification-form-priority-select"
                                    value={formData.priority}
                                    onChange={(value) => handleFormChange('priority', value)}
                                    options={[
                                        { id: 'urgent', displayName: 'Urgent' },
                                        { id: 'important', displayName: 'Important' },
                                        { id: 'regular', displayName: 'Regular' }
                                    ]}
                                />
                            }
                        />
                    </Grid>

                    {/* Row 3: Delivery and Apply condition */}
                    <Grid item xs={12} md={6}>
                        <StandaloneFormInlineInput
                            label="Delivery"
                            formComponent={
                                <SimpleAutocomplete
                                    data-testid="notification-form-frequency-select"
                                    value={formData.frequency}
                                    onChange={(value) => handleFormChange('frequency', value)}
                                    options={[
                                        { id: 'real_time', displayName: 'Real Time' },
                                        { id: '10m', displayName: '10 minutes' },
                                        { id: '30m', displayName: '30 minutes' },
                                        { id: '60m', displayName: '60 minutes' },
                                        { id: 'daily', displayName: 'Daily' },
                                        { id: 'weekly', displayName: 'Weekly' }
                                    ]}
                                />
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <StandaloneFormInlineInput
                            label="Apply condition"
                            formComponent={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Switch
                                        data-testid="notification-form-condition-toggle"
                                        checked={showCondition}
                                        onChange={(e) => handleConditionToggle(e.target.checked)}
                                        disabled={!canApplyCondition}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#5850EC'
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: '#5850EC'
                                            }
                                        }}
                                    />
                                </Box>
                            }
                        />
                    </Grid>

                    {/* Condition Section */}
                    {showCondition && canApplyCondition && (
                        <Grid item xs={12}>
                            <ConditionSection
                                fields={fields}
                                loadingFields={loadingFields}
                                condition={formData.condition}
                                onChange={handleConditionChange}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>

            {/* Footer with Actions */}
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}>
                    {isDuplicate && (
                        <Box data-testid="notification-form-duplicate-error" sx={{ color: 'error.main' }}>
                            A notification with this configuration already exists
                        </Box>
                    )}
                    <Button
                        data-testid="notification-form-submit-button"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isLoading || !isFormValid}
                    >
                        {(() => {
                            if (isLoading) {
                                return <CircularProgress size={20} sx={{ color: '#fff' }} />;
                            }
                            if (notification) {
                                return 'Update Notification';
                            }
                            return 'Add Notification';
                        })()}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default NotificationForm;
